import { useState, useEffect, useCallback, useRef } from "react"
import { useAppContext } from "@ciq/shared/hooks/context"
import { fetchColorRanges, saveMapColors } from "@ciq/shared/api/mapSettings"
import { ErrorToast, SuccessToast } from "@ciq/shared/utils/toast"
import { Button } from "@ciq/shared/components/buttons"
import eventBus from "@ciq/shared/hooks/eventBus"
import "./MapSettingsTab.css"

/**
 * @typedef {Object} ColorValidation
 * @property {boolean} isEmpty - Whether the color value is empty
 * @property {boolean} isValidFormat - Whether the color is valid hex format
 * @property {boolean} isValid - Whether the color passes all validations
 */

/**
 * Validates a hex color string.
 * @param {string} value - The color value to validate
 * @returns {ColorValidation} Validation result object
 */
const validateColor = value => {
  const isEmpty = value.length === 0
  const isValidFormat = /^#[0-9a-fA-F]{6}$/i.test(value)
  return { isEmpty, isValidFormat, isValid: !isEmpty && isValidFormat }
}

/**
 * @typedef {Object} ColorInputProps
 * @property {string} label - Display label for the color input
 * @property {string} value - Current hex color value
 * @property {(value: string) => void} onChange - Callback when value changes
 * @property {string} testId - Test ID for the input container
 */

/**
 * Individual color input with validation feedback.
 * @param {ColorInputProps} props - Component props
 * @returns {JSX.Element} Color input component
 */
function ColorInput({ label, value, onChange, testId }) {
  const { isEmpty, isValidFormat, isValid } = validateColor(value)

  let errorMessage = null
  if (isEmpty) {
    errorMessage = "Required"
  } else if (!isValidFormat) {
    errorMessage = "Invalid hex format"
  }

  return (
    <div className="map-settings-color-input" data-testid={testId}>
      <label>{label}</label>
      <div className="color-input-container">
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          className={isValid ? "" : "invalid"}
          aria-invalid={!isValid}
          aria-describedby={errorMessage ? `${testId}-error` : undefined}
        />
        <div
          className="color-swatch"
          style={{ backgroundColor: isValid ? value : "#EEE" }}
          aria-label={`Color preview: ${isValid ? value : "invalid"}`}
        />
        {errorMessage && (
          <span id={`${testId}-error`} className="color-error" role="alert">
            {errorMessage}
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * Color keys in display order (indices match API percentage array).
 */
const COLOR_KEYS = ["inactive", "low", "moderate", "active", "high"]

/**
 * @typedef {Object} ColorState
 * @property {string} inactive - Inactive color hex value
 * @property {string} low - Low color hex value
 * @property {string} moderate - Moderate color hex value
 * @property {string} active - Active color hex value
 * @property {string} high - High color hex value
 */

/**
 * @typedef {Object} LabelState
 * @property {string} inactive - Inactive label from API
 * @property {string} low - Low label from API
 * @property {string} moderate - Moderate label from API
 * @property {string} active - Active label from API
 * @property {string} high - High label from API
 */

/**
 * MapSettingsTab - Tab content for customizing map legend colors.
 *
 * Follows modules/web/CLAUDE.md patterns:
 * - Uses addLoadingId/removeLoadingId for async operations
 * - try-catch with ErrorToast for failures
 * - State management with useState
 *
 * @returns {JSX.Element} Map settings tab component
 */
export function MapSettingsTab() {
  const { schoolId, addLoadingId, removeLoadingId } = useAppContext()

  // Form state
  /** @type {[ColorState, React.Dispatch<React.SetStateAction<ColorState>>]} */
  const [colors, setColors] = useState({
    inactive: "",
    low: "",
    moderate: "",
    active: "",
    high: "",
  })

  // Labels from API (display threshold percentages from database)
  /** @type {[LabelState, React.Dispatch<React.SetStateAction<LabelState>>]} */
  const [labels, setLabels] = useState({
    inactive: "",
    low: "",
    moderate: "",
    active: "",
    high: "",
  })

  // Original colors for "Reset to Saved" functionality
  /** @type {[ColorState|null, React.Dispatch<React.SetStateAction<ColorState|null>>]} */
  const [savedColors, setSavedColors] = useState(null)

  // Loading/error states
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [loadError, setLoadError] = useState(null)

  // Load current colors on mount
  useEffect(() => {
    if (!schoolId) return

    const controller = new AbortController()
    const loadingId = "MapSettingsTab#load"

    const loadColors = async () => {
      setIsLoading(true)
      setLoadError(null)
      addLoadingId(loadingId)

      try {
        const colorRanges = await fetchColorRanges(schoolId, controller.signal)

        // Extract colors and labels from percentage ranges (5 colors)
        const loadedColors = {
          inactive: colorRanges.percentage[0].color,
          low: colorRanges.percentage[1].color,
          moderate: colorRanges.percentage[2].color,
          active: colorRanges.percentage[3].color,
          high: colorRanges.percentage[4].color,
        }

        // Extract labels from API response (threshold percentages from database)
        const loadedLabels = {
          inactive: colorRanges.percentage[0].label,
          low: colorRanges.percentage[1].label,
          moderate: colorRanges.percentage[2].label,
          active: colorRanges.percentage[3].label,
          high: colorRanges.percentage[4].label,
        }

        setColors(loadedColors)
        setLabels(loadedLabels)
        setSavedColors(loadedColors)
      } catch (e) {
        if (e.name === "AbortError") return
        console.error("Failed to load map colors:", e)
        setLoadError("Failed to load color settings")
        if (!e.handled) {
          ErrorToast("Failed to load color settings")
        }
      } finally {
        setIsLoading(false)
        removeLoadingId(loadingId)
      }
    }

    loadColors()

    return () => controller.abort()
  }, [schoolId, addLoadingId, removeLoadingId])

  // Handle color change
  const handleColorChange = useCallback((key, value) => {
    setColors(prev => ({ ...prev, [key]: value }))
  }, [])

  // Check if form is valid
  const isFormValid = Object.values(colors).every(value => validateColor(value).isValid)

  // Check if form has changes
  const hasChanges = savedColors && JSON.stringify(colors) !== JSON.stringify(savedColors)

  // Handle save
  // NOTE: Uses AbortController to prevent state updates on unmounted component
  const saveControllerRef = useRef(null)

  const handleSave = async () => {
    if (!isFormValid || !hasChanges) return

    // Abort any in-flight save
    saveControllerRef.current?.abort()
    saveControllerRef.current = new AbortController()

    const loadingId = "MapSettingsTab#save"
    setIsSaving(true)
    addLoadingId(loadingId)

    try {
      const colorRanges = await saveMapColors(
        schoolId,
        colors,
        saveControllerRef.current.signal
      )

      // Update saved state with response
      const newSavedColors = {
        inactive: colorRanges.percentage[0].color,
        low: colorRanges.percentage[1].color,
        moderate: colorRanges.percentage[2].color,
        active: colorRanges.percentage[3].color,
        high: colorRanges.percentage[4].color,
      }

      setColors(newSavedColors)
      setSavedColors(newSavedColors)
      SuccessToast("Map colors saved successfully")

      // Trigger map data refresh to apply new colors
      eventBus.emit("athena-datasource-updated")
    } catch (e) {
      if (e.name === "AbortError") return
      console.error("Failed to save map colors:", e)
      if (!e.handled) {
        ErrorToast("Failed to save color settings")
      }
    } finally {
      setIsSaving(false)
      removeLoadingId(loadingId)
    }
  }

  // Clean up save controller on unmount
  useEffect(() => {
    return () => saveControllerRef.current?.abort()
  }, [])

  // Handle reset to saved values
  const handleReset = useCallback(() => {
    if (savedColors) {
      setColors(savedColors)
    }
  }, [savedColors])

  // Render loading state
  if (isLoading) {
    return (
      <div className="map-settings-tab" data-testid="map-settings-tab">
        <div className="map-settings-loading">Loading color settings...</div>
      </div>
    )
  }

  // Render error state
  if (loadError) {
    return (
      <div className="map-settings-tab" data-testid="map-settings-tab">
        <div className="map-settings-error" role="alert">
          {loadError}
        </div>
      </div>
    )
  }

  return (
    <div className="map-settings-tab" data-testid="map-settings-tab">
      <div className="map-settings-header">
        <h3>Map Legend Colors</h3>
        <p className="map-settings-description">
          Customize the colors used to display utilization levels on the map.
        </p>
      </div>

      <div className="map-settings-colors">
        {COLOR_KEYS.map(key => (
          <ColorInput
            key={key}
            label={labels[key] || key}
            value={colors[key]}
            onChange={value => handleColorChange(key, value)}
            testId={`color-input-${key}`}
          />
        ))}
      </div>

      <div className="map-settings-actions">
        <Button
          onClick={handleReset}
          disabled={!hasChanges || isSaving}
          data-testid="reset-button"
        >
          Reset to Saved
        </Button>
        <Button
          onClick={handleSave}
          disabled={!isFormValid || !hasChanges || isSaving}
          variant="primary"
          data-testid="save-button"
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  )
}

export default MapSettingsTab
